import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Divider,
  TablePagination,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Build as BuildIcon,
  SwapHoriz as SwapHorizIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { collection, getDocs, updateDoc, doc, query, writeBatch, getDoc } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { validateDatabase } from "../../../../Utils/databaseGuard";
import Swal from "sweetalert2";
import dayjs from "dayjs";

// Constants
const DECEMBER_2024 = new Date("2024-12-01T00:00:00Z");

const LEDGER_TYPES = {
  0: "Deposit",
  1: "Withdrawal (Legacy)",
  2: "Withdrawal",
  3: "Performance",
};

const SUGGESTED_TYPES = {
  0: "Deposit",
  1: "Withdrawal",
  2: "Withdrawal", 
  3: "Performance",
};

const PROBLEM_CATEGORIES = {
  ZERO_AMOUNT_WITHDRAWAL: "Zero Amount Withdrawal",
  ZERO_AMOUNT_DEPOSIT: "Zero Amount Deposit",
  ZERO_AMOUNT_UNKNOWN: "Zero Amount Unknown Type",
  NONZERO_OLD_WITHDRAWAL: "Non-Zero Old Withdrawal",
  LEGACY_TYPE: "Legacy Type (1)",
  NONZERO_DEPOSIT: "Non-Zero Orphan Deposit",
  OTHER: "Other",
};

// Category explanations for UI
const CATEGORY_EXPLANATIONS = {
  "Zero Amount Withdrawal": "Ledgers marked as 'withdrawal' but with $0.00 amount. These are likely PERFORMANCE snapshots (balance updates) that were incorrectly typed as withdrawals. A real withdrawal would have an amount.",
  "Zero Amount Deposit": "Ledgers marked as 'deposit' but with $0.00 amount. These are likely PERFORMANCE snapshots that were incorrectly typed as deposits. A real deposit would have an amount.",
  "Zero Amount Unknown Type": "Ledgers with null/undefined transaction type and $0.00 amount. These are PERFORMANCE snapshots with missing type field.",
  "Non-Zero Old Withdrawal": "Ledgers from OLD company data (before Dec 2024) marked as 'withdrawal' but with NO matching withdrawal transaction in the system. Investigation shows these are PERFORMANCE entries (monthly gains) incorrectly typed as withdrawals.",
  "Legacy Type (1)": "Old format withdrawal type (1) that was used before the current system. May be actual withdrawals or performance entries.",
  "Non-Zero Orphan Deposit": "Ledgers marked as 'deposit' with an amount but NO matching deposit transaction. Requires investigation - may be a missing transaction record.",
  "Other": "Edge cases that don't fit other categories. Requires manual review.",
};

const PHASES = {
  1: { 
    name: "Phase 1: Low Risk", 
    color: "#2af598", 
    description: "Convert $0.00 entries to performance",
    explanation: "WHY SAFE: $0.00 amount means no money moved. These are balance snapshots (like taking a photo of the balance) that were incorrectly labeled. Correcting the TYPE doesn't change any numbers."
  },
  2: { 
    name: "Phase 2: Old Data", 
    color: "#ff9800", 
    description: "Convert old non-zero entries to performance",
    explanation: "WHY MEDIUM RISK: These have amounts but NO matching transaction. Old company was recording monthly PERFORMANCE (gains/losses) as 'withdrawals'. Evidence: amounts match typical monthly performance percentages, and there are ZERO withdrawal transactions for these users."
  },
  3: { 
    name: "Phase 3: Investigation", 
    color: "#f52a2d", 
    description: "Edge cases requiring manual review",
    explanation: "WHY HIGH RISK: These don't fit the clear patterns. Could be missing transaction records or data entry errors. Each needs individual investigation."
  },
};

const LedgerStandardizationTool = () => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [problems, setProblems] = useState([]);
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [beforeDec2024Filter, setBeforeDec2024Filter] = useState("all");
  const [targetType, setTargetType] = useState(3); // Default to performance
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [userDataMap, setUserDataMap] = useState(new Map());
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({
    totalLedgers: 0,
    problemLedgers: 0,
    affectedUsers: 0,
    phase1Count: 0,
    phase2Count: 0,
    phase3Count: 0,
    phase1Amount: 0,
    phase2Amount: 0,
    phase3Amount: 0,
  });

  // Helper functions
  const getDate = (dateField) => {
    if (!dateField) return null;
    if (dateField.toDate) return dateField.toDate();
    if (dateField.seconds) return new Date(dateField.seconds * 1000);
    if (typeof dateField === "string") return new Date(dateField);
    return null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const getLedgerTypeName = (type) => {
    if (type === null || type === undefined) return "UNKNOWN";
    return LEDGER_TYPES[type] || `UNKNOWN (${type})`;
  };

  // Check if a ledger matches any transaction
  const findMatchingTransaction = (ledger, transactions, tolerance = 0.01) => {
    const ledgerAmount = Math.abs(parseFloat(ledger.amount || 0));
    const ledgerDate = getDate(ledger.date);
    
    // Determine expected transaction type
    const expectedTxType = ledger.transection_type === 0 ? 0 : 1; // 0 = deposit, 1 = withdrawal
    
    return transactions.find((tx) => {
      if (tx.user_id !== ledger.user_id) return false;
      if (tx.request_type !== expectedTxType) return false;
      if (![2, 5].includes(tx.request_status)) return false; // Only approved or paid
      
      const txAmount = Math.abs(parseFloat(tx.total_amount || tx.amount || 0));
      const amountMatch = Math.abs(ledgerAmount - txAmount) <= tolerance ||
                          Math.abs(ledgerAmount - txAmount) / Math.max(ledgerAmount, txAmount, 1) <= 0.01;
      
      const txDate = getDate(tx.date || tx.created_at);
      if (!ledgerDate || !txDate) return amountMatch;
      
      const daysDiff = Math.abs(ledgerDate - txDate) / (1000 * 60 * 60 * 24);
      return amountMatch && daysDiff <= 7;
    });
  };

  // Categorize a problem ledger and generate analysis
  const categorizeProblem = (ledger, isBeforeDec2024, userTransactions, user) => {
    const amount = parseFloat(ledger.amount || 0);
    const type = ledger.transection_type;
    const balance = parseFloat(ledger.current_balance || ledger.balance || 0);
    const date = getDate(ledger.date);
    
    let phase = 3;
    let category = PROBLEM_CATEGORIES.OTHER;
    let suggestedType = 3; // Default to performance
    let suggestedTypeName = "Performance";
    let analysisNote = "";
    let confidence = "LOW";
    
    // Count user's actual transactions
    const depositTxCount = userTransactions.filter(tx => tx.request_type === 0 && [2, 5].includes(tx.request_status)).length;
    const withdrawalTxCount = userTransactions.filter(tx => tx.request_type === 1 && [2, 5].includes(tx.request_status)).length;
    
    // Phase 1: Zero amount entries
    if (amount === 0) {
      confidence = "HIGH";
      suggestedType = 3;
      suggestedTypeName = "Performance";
      
      if (type === 2 || type === 1) {
        phase = 1;
        category = PROBLEM_CATEGORIES.ZERO_AMOUNT_WITHDRAWAL;
        analysisNote = `$0 amount = no money moved. This is a BALANCE SNAPSHOT incorrectly labeled as withdrawal. Current balance: ${formatCurrency(balance)}. Safe to convert to Performance.`;
      } else if (type === 0) {
        phase = 1;
        category = PROBLEM_CATEGORIES.ZERO_AMOUNT_DEPOSIT;
        analysisNote = `$0 amount = no money moved. This is a BALANCE SNAPSHOT incorrectly labeled as deposit. Current balance: ${formatCurrency(balance)}. Safe to convert to Performance.`;
      } else if (type === null || type === undefined || ![0, 1, 2, 3].includes(type)) {
        phase = 1;
        category = PROBLEM_CATEGORIES.ZERO_AMOUNT_UNKNOWN;
        analysisNote = `Missing transaction type (null/undefined) with $0 amount. This is a BALANCE SNAPSHOT with no type assigned. Current balance: ${formatCurrency(balance)}. Safe to convert to Performance.`;
      }
    }
    // Phase 2: Non-zero old data before Dec 2024
    else if (isBeforeDec2024 && (type === 2 || type === 1)) {
      if (type === 2) {
        phase = 2;
        category = PROBLEM_CATEGORIES.NONZERO_OLD_WITHDRAWAL;
        
        // Analyze if this looks like performance
        if (withdrawalTxCount === 0) {
          confidence = "HIGH";
          suggestedType = 3;
          suggestedTypeName = "Performance";
          analysisNote = `User has ${withdrawalTxCount} actual withdrawal transactions but this ledger is marked as withdrawal. This is OLD COMPANY DATA - they were recording monthly PERFORMANCE gains as "withdrawals". Amount ${formatCurrency(amount)} is likely a monthly performance payment. Convert to Performance.`;
        } else {
          confidence = "MEDIUM";
          suggestedType = 3;
          suggestedTypeName = "Performance";
          analysisNote = `User has ${withdrawalTxCount} withdrawal transaction(s) but this specific ledger has NO matching transaction. Date: ${date ? dayjs(date).format("MMM YYYY") : "N/A"}. Likely a PERFORMANCE entry mislabeled as withdrawal. Verify before converting.`;
        }
      } else if (type === 1) {
        phase = 2;
        category = PROBLEM_CATEGORIES.LEGACY_TYPE;
        confidence = "MEDIUM";
        
        // Check if there's a matching withdrawal
        const hasMatchingWithdrawal = userTransactions.some(tx => 
          tx.request_type === 1 && 
          [2, 5].includes(tx.request_status) &&
          Math.abs(parseFloat(tx.total_amount || tx.amount || 0) - amount) < 1
        );
        
        if (hasMatchingWithdrawal) {
          suggestedType = 2;
          suggestedTypeName = "Withdrawal";
          analysisNote = `Legacy type (1) with amount ${formatCurrency(amount)}. Found a MATCHING withdrawal transaction. This appears to be a real withdrawal - convert to modern Withdrawal type (2).`;
        } else {
          suggestedType = 3;
          suggestedTypeName = "Performance";
          analysisNote = `Legacy type (1) with amount ${formatCurrency(amount)}. NO matching withdrawal transaction found. Likely a PERFORMANCE entry using old type. Convert to Performance.`;
        }
      }
    }
    // Phase 3: Non-zero deposit without matching transaction
    else if (type === 0 && amount !== 0) {
      phase = 3;
      category = PROBLEM_CATEGORIES.NONZERO_DEPOSIT;
      confidence = "LOW";
      
      // Check if there's a matching deposit transaction
      const hasMatchingDeposit = userTransactions.some(tx => 
        tx.request_type === 0 && 
        [2, 5].includes(tx.request_status) &&
        Math.abs(parseFloat(tx.total_amount || tx.amount || 0) - amount) < 1
      );
      
      if (hasMatchingDeposit) {
        suggestedType = 0;
        suggestedTypeName = "Deposit";
        analysisNote = `Deposit ledger with amount ${formatCurrency(amount)}. Found a matching deposit transaction but date/amount didn't match exactly. VERIFY: This may be a valid deposit with date discrepancy.`;
      } else if (depositTxCount === 0) {
        suggestedType = 3;
        suggestedTypeName = "Performance";
        analysisNote = `User has ${depositTxCount} deposit transactions but this ${formatCurrency(amount)} deposit ledger exists. INVESTIGATE: Could be missing transaction record OR mislabeled performance.`;
      } else {
        suggestedType = 0;
        suggestedTypeName = "Deposit (verify)";
        analysisNote = `Deposit ledger ${formatCurrency(amount)} with no exact match. User has ${depositTxCount} deposit transaction(s). INVESTIGATE: May be a valid deposit with data discrepancy.`;
      }
    }
    // Other edge cases
    else {
      phase = 3;
      category = PROBLEM_CATEGORIES.OTHER;
      confidence = "LOW";
      suggestedType = 3;
      suggestedTypeName = "Unknown";
      analysisNote = `Edge case: Type ${type}, Amount ${formatCurrency(amount)}, Before Dec 2024: ${isBeforeDec2024}. Requires manual investigation. Check user's transaction history.`;
    }
    
    return { 
      phase, 
      category, 
      suggestedType, 
      suggestedTypeName, 
      analysisNote, 
      confidence,
      depositTxCount,
      withdrawalTxCount
    };
  };

  // Load user data
  const loadUserData = async () => {
    const userMap = new Map();
    try {
      const usersSnapshot = await getDocs(collection(DATABASE, "users"));
      usersSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        userMap.set(doc.id, {
          id: doc.id,
          name: data.full_name || data.first_name || "Unknown User",
          email: data.email || "Unknown Email",
          user_type: data.user_type !== undefined ? data.user_type : 0,
          net_balance: parseFloat(data.net_balance || 0),
        });
      });
    } catch (error) {
      console.error("Error loading user data:", error);
    }
    return userMap;
  };

  // Main analysis function
  const handleAnalyze = async () => {
    validateDatabase();
    setAnalyzing(true);
    setAnalysisComplete(false);
    setProblems([]);
    setSelectedProblems(new Set());
    setProgress(0);

    try {
      console.log("🔍 Starting comprehensive ledger analysis for ALL users...");

      // Load all users
      setProgress(5);
      console.log("📥 Loading user data...");
      const userMap = await loadUserData();
      setUserDataMap(userMap);
      console.log(`   ✅ ${userMap.size} users loaded`);

      // Load all ledgers
      setProgress(15);
      console.log("📥 Loading all ledgers...");
      const ledgersSnapshot = await getDocs(collection(DATABASE, "ledgers"));
      const allLedgers = ledgersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log(`   ✅ ${allLedgers.length} ledgers loaded`);

      // Load all transactions
      setProgress(30);
      console.log("📥 Loading all transactions...");
      const transactionsSnapshot = await getDocs(collection(DATABASE, "transection_requests"));
      const allTransactions = transactionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log(`   ✅ ${allTransactions.length} transactions loaded`);

      // Group transactions by user
      setProgress(40);
      const transactionsByUser = new Map();
      allTransactions.forEach((tx) => {
        const userId = tx.user_id;
        if (!userId) return;
        if (!transactionsByUser.has(userId)) {
          transactionsByUser.set(userId, []);
        }
        transactionsByUser.get(userId).push(tx);
      });

      // Analyze each ledger
      setProgress(50);
      console.log("🔍 Analyzing ledgers for ALL users...");
      const foundProblems = [];
      const affectedUsersSet = new Set();
      let validCount = 0;

      for (let i = 0; i < allLedgers.length; i++) {
        const ledger = allLedgers[i];
        const type = ledger.transection_type;
        const date = getDate(ledger.date);
        const isBeforeDec2024 = date && date < DECEMBER_2024;
        const user = userMap.get(ledger.user_id) || { email: "N/A", name: "Unknown" };
        const userTransactions = transactionsByUser.get(ledger.user_id) || [];

        // Update progress
        if (i % 200 === 0) {
          setProgress(50 + Math.floor((i / allLedgers.length) * 40));
        }

        // Skip performance ledgers - they're valid
        if (type === 3) {
          validCount++;
          continue;
        }

        // Check for unknown/invalid types
        if (type === null || type === undefined || ![0, 1, 2, 3].includes(type)) {
          const analysis = categorizeProblem(ledger, isBeforeDec2024, userTransactions, user);
          affectedUsersSet.add(ledger.user_id);
          foundProblems.push({
            id: ledger.id,
            ledger,
            userId: ledger.user_id,
            userEmail: user.email,
            userName: user.name,
            userInfo: user.id ? `ID: ${user.id.substring(0, 8)}... | Balance: ${formatCurrency(user.net_balance)}` : "No user data",
            type,
            typeName: getLedgerTypeName(type),
            date: date ? dayjs(date).format("MM/DD/YYYY") : "N/A",
            dateRaw: date,
            amount: parseFloat(ledger.amount || 0),
            currentBalance: parseFloat(ledger.current_balance || ledger.balance || 0),
            isBeforeDec2024,
            migrated: ledger._migrated_from || "No",
            phase: analysis.phase,
            category: analysis.category,
            suggestedType: analysis.suggestedType,
            suggestedTypeName: analysis.suggestedTypeName,
            analysisNote: analysis.analysisNote,
            confidence: analysis.confidence,
            depositTxCount: analysis.depositTxCount,
            withdrawalTxCount: analysis.withdrawalTxCount,
          });
          continue;
        }

        // Check deposit/withdrawal ledgers for matching transactions
        if ([0, 1, 2].includes(type)) {
          const matchingTx = findMatchingTransaction(ledger, userTransactions);
          
          if (!matchingTx) {
            const analysis = categorizeProblem(ledger, isBeforeDec2024, userTransactions, user);
            affectedUsersSet.add(ledger.user_id);
            foundProblems.push({
              id: ledger.id,
              ledger,
              userId: ledger.user_id,
              userEmail: user.email,
              userName: user.name,
              userInfo: user.id ? `ID: ${user.id.substring(0, 8)}... | Balance: ${formatCurrency(user.net_balance)}` : "No user data",
              type,
              typeName: getLedgerTypeName(type),
              date: date ? dayjs(date).format("MM/DD/YYYY") : "N/A",
              dateRaw: date,
              amount: parseFloat(ledger.amount || 0),
              currentBalance: parseFloat(ledger.current_balance || ledger.balance || 0),
              isBeforeDec2024,
              migrated: ledger._migrated_from || "No",
              phase: analysis.phase,
              category: analysis.category,
              suggestedType: analysis.suggestedType,
              suggestedTypeName: analysis.suggestedTypeName,
              analysisNote: analysis.analysisNote,
              confidence: analysis.confidence,
              depositTxCount: analysis.depositTxCount,
              withdrawalTxCount: analysis.withdrawalTxCount,
            });
          } else {
            validCount++;
          }
        }
      }

      // Calculate stats
      setProgress(95);
      const phase1 = foundProblems.filter((p) => p.phase === 1);
      const phase2 = foundProblems.filter((p) => p.phase === 2);
      const phase3 = foundProblems.filter((p) => p.phase === 3);

      setStats({
        totalLedgers: allLedgers.length,
        problemLedgers: foundProblems.length,
        validLedgers: validCount,
        affectedUsers: affectedUsersSet.size,
        phase1Count: phase1.length,
        phase2Count: phase2.length,
        phase3Count: phase3.length,
        phase1Amount: phase1.reduce((sum, p) => sum + Math.abs(p.amount), 0),
        phase2Amount: phase2.reduce((sum, p) => sum + Math.abs(p.amount), 0),
        phase3Amount: phase3.reduce((sum, p) => sum + Math.abs(p.amount), 0),
      });

      setProblems(foundProblems);
      setAnalysisComplete(true);
      setProgress(100);

      console.log(`✅ Analysis complete for ALL ${userMap.size} users`);
      console.log(`   Found ${foundProblems.length} problem ledgers across ${affectedUsersSet.size} users`);
      console.log(`   Phase 1: ${phase1.length} | Phase 2: ${phase2.length} | Phase 3: ${phase3.length}`);

    } catch (error) {
      console.error("❌ Error analyzing ledgers:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to analyze ledgers: ${error.message}`,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = new Set(filteredProblems.map((p) => p.id));
      setSelectedProblems(allIds);
    } else {
      setSelectedProblems(new Set());
    }
  };

  const handleSelectProblem = (problemId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    setSelectedProblems((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(problemId)) {
        newSelected.delete(problemId);
      } else {
        newSelected.add(problemId);
      }
      return newSelected;
    });
  };

  const handleSelectPhase = (phase) => {
    const phaseProblems = filteredProblems.filter((p) => p.phase === phase);
    const allSelected = phaseProblems.every((p) => selectedProblems.has(p.id));
    
    setSelectedProblems((prev) => {
      const newSelected = new Set(prev);
      phaseProblems.forEach((p) => {
        if (allSelected) {
          newSelected.delete(p.id);
        } else {
          newSelected.add(p.id);
        }
      });
      return newSelected;
    });
  };

  // Execute corrections
  const handleExecute = async () => {
    if (selectedProblems.size === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select ledgers to correct",
      });
      return;
    }

    const selectedItems = problems.filter((p) => selectedProblems.has(p.id));
    const phase3Items = selectedItems.filter((p) => p.phase === 3);

    if (phase3Items.length > 0) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Phase 3 Items Selected",
        html: `You have selected ${phase3Items.length} Phase 3 items that require investigation.<br/><br/>Are you sure you want to proceed with converting these to performance?`,
        showCancelButton: true,
        confirmButtonText: "Yes, Proceed",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#f52a2d",
      });

      if (!result.isConfirmed) return;
    }

    setExecuteDialogOpen(true);
  };

  const confirmExecute = async () => {
    try {
      setLoading(true);

      const selectedItems = problems.filter((p) => selectedProblems.has(p.id));
      const batch = writeBatch(DATABASE);
      let corrected = 0;

      for (const item of selectedItems) {
        const docRef = doc(DATABASE, "ledgers", item.id);
        // Use suggested type if available, otherwise use target type
        const newType = targetType;
        batch.update(docRef, {
          transection_type: newType,
          _standardized_at: new Date(),
          _standardized_from: item.type,
          _standardized_to: newType,
          _standardized_reason: `${item.analysisNote} | Phase ${item.phase} correction.`,
          _standardized_by: "LedgerStandardizationTool",
          _standardized_confidence: item.confidence,
        });
        corrected++;
      }

      await batch.commit();

      // Group by user for summary
      const userImpact = new Map();
      selectedItems.forEach((item) => {
        if (!userImpact.has(item.userId)) {
          userImpact.set(item.userId, 0);
        }
        userImpact.set(item.userId, userImpact.get(item.userId) + 1);
      });

      Swal.fire({
        icon: "success",
        title: "Corrections Applied",
        html: `
          <div style="text-align: left;">
            <p><strong>Successfully corrected ${corrected} ledgers</strong></p>
            <p>Affected users: ${userImpact.size}</p>
            <hr/>
            <p><strong>What was done:</strong></p>
            <ul>
              <li>Changed transection_type to 3 (performance)</li>
              <li>Added _standardized_at timestamp</li>
              <li>Added _standardized_from (original type)</li>
              <li>Added _standardized_reason</li>
            </ul>
            <p><strong>Note:</strong> Client balances are NOT affected. Only the transaction type label was corrected.</p>
          </div>
        `,
        width: 600,
      });

      setSelectedProblems(new Set());
      setExecuteDialogOpen(false);
      handleAnalyze(); // Refresh
    } catch (error) {
      console.error("❌ Error executing corrections:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to execute corrections: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtering
  const filteredProblems = problems.filter((p) => {
    // Phase filter
    if (phaseFilter !== "all" && p.phase !== parseInt(phaseFilter)) return false;

    // Category filter
    if (categoryFilter !== "all" && p.category !== categoryFilter) return false;

    // Before Dec 2024 filter
    if (beforeDec2024Filter === "yes" && !p.isBeforeDec2024) return false;
    if (beforeDec2024Filter === "no" && p.isBeforeDec2024) return false;

    // User filter
    if (userFilter) {
      const searchTerm = userFilter.toLowerCase();
      return (
        p.userId.toLowerCase().includes(searchTerm) ||
        p.userName.toLowerCase().includes(searchTerm) ||
        p.userEmail.toLowerCase().includes(searchTerm)
      );
    }

    return true;
  });

  const paginatedProblems = filteredProblems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get unique categories for filter
  const uniqueCategories = [...new Set(problems.map((p) => p.category))];

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" sx={{ color: "#fff" }}>
          Ledger Standardization Tool
        </Typography>
        <Button
          variant="contained"
          startIcon={analyzing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleAnalyze}
          disabled={analyzing}
          sx={{
            background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
            "&:hover": {
              background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
              opacity: 0.9,
            },
          }}
        >
          {analyzing ? "Analyzing ALL Users..." : "Analyze All Ledgers"}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2, backgroundColor: "rgba(33, 150, 243, 0.1)", color: "#2196f3" }}>
        <Typography variant="body2">
          <strong>📋 Purpose:</strong> This tool identifies and corrects ledgers with incorrect transaction types.
          <br /><br />
          <strong>✅ What it does:</strong> Analyzes ALL users to find orphan ledgers (no matching transaction) and suggests the correct type.
          <br />
          <strong>🛡️ Safety:</strong> Client balances are NOT affected. Only the transaction type label is corrected.
          <br />
          <strong>📊 Tracking:</strong> All changes are tracked with <code>_standardized_at</code>, <code>_standardized_from</code>, <code>_standardized_to</code>, and <code>_standardized_reason</code> fields.
        </Typography>
      </Alert>

      {/* Category Explanations */}
      <Accordion sx={{ mb: 3, backgroundColor: "rgba(255, 152, 0, 0.1)", border: "1px solid rgba(255, 152, 0, 0.3)" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#ff9800" }} />}>
          <Typography sx={{ color: "#ff9800", fontWeight: "bold" }}>
            📖 Category Definitions & Explanations (Click to expand)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {Object.entries(CATEGORY_EXPLANATIONS).map(([cat, explanation]) => (
              <Grid item xs={12} md={6} key={cat}>
                <Box sx={{ p: 2, backgroundColor: "rgba(0, 0, 0, 0.2)", borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: "bold", mb: 1 }}>
                    {cat}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>
                    {explanation}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 2, borderColor: "rgba(255, 255, 255, 0.1)" }} />
          <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: "bold", mb: 1 }}>
            Phase Explanations:
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(PHASES).map(([phaseNum, phase]) => (
              <Grid item xs={12} md={4} key={phaseNum}>
                <Box sx={{ p: 2, backgroundColor: `${phase.color}22`, borderRadius: 1, border: `1px solid ${phase.color}44` }}>
                  <Typography variant="subtitle2" sx={{ color: phase.color, fontWeight: "bold", mb: 1 }}>
                    Phase {phaseNum}: {phase.name.replace(`Phase ${phaseNum}: `, "")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
                    {phase.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#888" }}>
                    {phase.explanation}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {analyzing && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
            Analyzing all ledgers for ALL users... {progress}%
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}

      {analysisComplete && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(255, 255, 255, 0.2)" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ color: "#fff" }}>{stats.totalLedgers}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Total Ledgers</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(255, 152, 0, 0.2)" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ color: "#ff9800" }}>{stats.problemLedgers}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Problem Ledgers</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(33, 150, 243, 0.2)" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ color: "#2196f3" }}>{stats.affectedUsers}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Affected Users</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: `1px solid ${PHASES[1].color}33` }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ color: PHASES[1].color }}>{stats.phase1Count}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Phase 1 (Low Risk)</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: `1px solid ${PHASES[2].color}33` }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ color: PHASES[2].color }}>{stats.phase2Count}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Phase 2 (Old Data)</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: `1px solid ${PHASES[3].color}33` }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ color: PHASES[3].color }}>{stats.phase3Count}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Phase 3 (Investigate)</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Phase Selection Buttons */}
          <Card sx={{ mb: 3, background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
                Quick Phase Selection
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => handleSelectPhase(1)}
                    sx={{
                      color: PHASES[1].color,
                      borderColor: PHASES[1].color,
                      p: 2,
                      "&:hover": { backgroundColor: `${PHASES[1].color}22` },
                    }}
                  >
                    <Box sx={{ textAlign: "left", width: "100%" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Select All Phase 1
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#aaa" }}>
                        {stats.phase1Count} ledgers | {formatCurrency(stats.phase1Amount)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#888" }}>
                        LOW RISK - $0.00 entries
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => handleSelectPhase(2)}
                    sx={{
                      color: PHASES[2].color,
                      borderColor: PHASES[2].color,
                      p: 2,
                      "&:hover": { backgroundColor: `${PHASES[2].color}22` },
                    }}
                  >
                    <Box sx={{ textAlign: "left", width: "100%" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Select All Phase 2
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#aaa" }}>
                        {stats.phase2Count} ledgers | {formatCurrency(stats.phase2Amount)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#888" }}>
                        MEDIUM RISK - Old data (before Dec 2024)
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => handleSelectPhase(3)}
                    sx={{
                      color: PHASES[3].color,
                      borderColor: PHASES[3].color,
                      p: 2,
                      "&:hover": { backgroundColor: `${PHASES[3].color}22` },
                    }}
                  >
                    <Box sx={{ textAlign: "left", width: "100%" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Select All Phase 3
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#aaa" }}>
                        {stats.phase3Count} ledgers | {formatCurrency(stats.phase3Amount)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#888" }}>
                        HIGH RISK - Requires investigation
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Filters and Actions */}
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              label="Filter by User Name, Email, or ID"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              size="small"
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                },
                "& .MuiInputLabel-root": { color: "#aaa" },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ color: "#aaa" }}>Phase</InputLabel>
              <Select
                value={phaseFilter}
                label="Phase"
                onChange={(e) => setPhaseFilter(e.target.value)}
                sx={{ color: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 255, 255, 0.3)" } }}
              >
                <MenuItem value="all">All Phases</MenuItem>
                <MenuItem value="1">Phase 1 (Low Risk)</MenuItem>
                <MenuItem value="2">Phase 2 (Old Data)</MenuItem>
                <MenuItem value="3">Phase 3 (Investigate)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ color: "#aaa" }}>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
                sx={{ color: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 255, 255, 0.3)" } }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {uniqueCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: "#aaa" }}>Before Dec 2024</InputLabel>
              <Select
                value={beforeDec2024Filter}
                label="Before Dec 2024"
                onChange={(e) => setBeforeDec2024Filter(e.target.value)}
                sx={{ color: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 255, 255, 0.3)" } }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes (Old Data)</MenuItem>
                <MenuItem value="no">No (New Data)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {/* Action Row */}
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", p: 2, backgroundColor: "rgba(42, 245, 152, 0.05)", borderRadius: 1, border: "1px solid rgba(42, 245, 152, 0.2)" }}>
            <Typography variant="body2" sx={{ color: "#aaa" }}>
              Convert selected to:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
                sx={{ color: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(42, 245, 152, 0.5)" } }}
              >
                <MenuItem value={0}>Deposit (type 0)</MenuItem>
                <MenuItem value={2}>Withdrawal (type 2)</MenuItem>
                <MenuItem value={3}>Performance (type 3)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<SwapHorizIcon />}
              onClick={handleExecute}
              disabled={selectedProblems.size === 0 || loading}
              sx={{
                background: "linear-gradient(148.97deg, #2af598 0%, #00c820 100%)",
                color: "#000",
                "&:hover": {
                  background: "linear-gradient(148.97deg, #2af598 0%, #00c820 100%)",
                  opacity: 0.9,
                },
                "&:disabled": {
                  background: "rgba(255, 255, 255, 0.1)",
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Convert {selectedProblems.size} Selected to {LEDGER_TYPES[targetType]}
            </Button>
            <Typography variant="caption" sx={{ color: "#888" }}>
              Tip: Review the "Suggested Type" column for each ledger before converting.
            </Typography>
          </Box>

          {/* Problems Table */}
          <TableContainer component={Paper} sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={filteredProblems.length > 0 && filteredProblems.every((p) => selectedProblems.has(p.id))}
                      indeterminate={filteredProblems.some((p) => selectedProblems.has(p.id)) && !filteredProblems.every((p) => selectedProblems.has(p.id))}
                      onChange={handleSelectAll}
                      sx={{ color: "#fff" }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Phase</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>User</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Current Type</TableCell>
                  <TableCell sx={{ color: "#2af598", fontWeight: "bold" }}>Suggested Type</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Confidence</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Amount</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Old Data?</TableCell>
                  <TableCell sx={{ color: "#ff9800", fontWeight: "bold", minWidth: 300 }}>Analysis Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProblems.map((problem) => (
                  <TableRow
                    key={problem.id}
                    sx={{
                      backgroundColor: selectedProblems.has(problem.id) ? "rgba(42, 245, 152, 0.1)" : "transparent",
                      "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProblems.has(problem.id)}
                        onChange={(e) => handleSelectProblem(problem.id, e)}
                        sx={{ color: "#fff" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`P${problem.phase}`}
                        size="small"
                        sx={{
                          backgroundColor: `${PHASES[problem.phase].color}33`,
                          color: PHASES[problem.phase].color,
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#fff" }}>
                      <Tooltip title={problem.userInfo || `User ID: ${problem.userId}`} arrow>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "bold", color: problem.userName === "Unknown" ? "#f52a2d" : "#fff" }}>
                            {problem.userName === "Unknown" ? "⚠️ Unknown User" : problem.userName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#aaa" }}>
                            {problem.userEmail}
                          </Typography>
                          {problem.userName === "Unknown" && (
                            <Typography variant="caption" sx={{ color: "#888", display: "block" }}>
                              ID: {problem.userId.substring(0, 12)}...
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                            Deposits: {problem.depositTxCount || 0} | Withdrawals: {problem.withdrawalTxCount || 0}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={problem.typeName}
                        size="small"
                        sx={{
                          backgroundColor: problem.type === null || problem.type === undefined ? "rgba(245, 42, 45, 0.3)" : "rgba(255, 152, 0, 0.3)",
                          color: "#fff",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={problem.suggestedTypeName}
                        size="small"
                        sx={{
                          backgroundColor: problem.suggestedType === 3 ? "rgba(42, 245, 152, 0.3)" : 
                                          problem.suggestedType === 0 ? "rgba(33, 150, 243, 0.3)" :
                                          problem.suggestedType === 2 ? "rgba(255, 152, 0, 0.3)" : "rgba(128, 128, 128, 0.3)",
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={problem.confidence}
                        size="small"
                        sx={{
                          backgroundColor: problem.confidence === "HIGH" ? "rgba(42, 245, 152, 0.3)" : 
                                          problem.confidence === "MEDIUM" ? "rgba(255, 152, 0, 0.3)" : "rgba(245, 42, 45, 0.3)",
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#fff" }}>{problem.date}</TableCell>
                    <TableCell sx={{ color: problem.amount === 0 ? "#888" : "#fff", fontWeight: problem.amount === 0 ? "normal" : "bold" }}>
                      {formatCurrency(problem.amount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={problem.isBeforeDec2024 ? "OLD" : "NEW"}
                        size="small"
                        sx={{
                          backgroundColor: problem.isBeforeDec2024 ? "rgba(255, 152, 0, 0.3)" : "rgba(42, 245, 152, 0.3)",
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#aaa", fontSize: "0.8rem", maxWidth: 400 }}>
                      <Typography variant="caption" sx={{ color: "#ccc", whiteSpace: "pre-wrap" }}>
                        {problem.analysisNote}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredProblems.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{ color: "#fff" }}
            />
          </TableContainer>
        </>
      )}

      {/* Execute Confirmation Dialog */}
      <Dialog open={executeDialogOpen} onClose={() => setExecuteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: "#fff", background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          Confirm Corrections
        </DialogTitle>
        <DialogContent sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", color: "#fff" }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1">
              You are about to convert <strong>{selectedProblems.size}</strong> ledgers to <strong>{LEDGER_TYPES[targetType]}</strong> (type {targetType}).
            </Typography>
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>What will happen:</strong>
          </Typography>
          <ul style={{ color: "#aaa" }}>
            <li>transection_type will be changed to {targetType} ({LEDGER_TYPES[targetType]})</li>
            <li>_standardized_at, _standardized_from, _standardized_to, and _standardized_reason fields will be added</li>
            <li>_standardized_confidence will record the analysis confidence level</li>
            <li>Client balances will NOT be affected</li>
            <li>Changes can be tracked via the _standardized_* fields</li>
          </ul>

          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            <strong>Breakdown by Phase:</strong>
          </Typography>
          {[1, 2, 3].map((phase) => {
            const count = problems.filter((p) => selectedProblems.has(p.id) && p.phase === phase).length;
            if (count === 0) return null;
            return (
              <Chip
                key={phase}
                label={`Phase ${phase}: ${count} ledgers`}
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: `${PHASES[phase].color}33`,
                  color: PHASES[phase].color,
                }}
              />
            );
          })}
          
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            <strong>Breakdown by Confidence:</strong>
          </Typography>
          {["HIGH", "MEDIUM", "LOW"].map((conf) => {
            const count = problems.filter((p) => selectedProblems.has(p.id) && p.confidence === conf).length;
            if (count === 0) return null;
            return (
              <Chip
                key={conf}
                label={`${conf}: ${count} ledgers`}
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: conf === "HIGH" ? "rgba(42, 245, 152, 0.3)" : 
                                  conf === "MEDIUM" ? "rgba(255, 152, 0, 0.3)" : "rgba(245, 42, 45, 0.3)",
                  color: "#fff",
                }}
              />
            );
          })}
        </DialogContent>
        <DialogActions sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          <Button onClick={() => setExecuteDialogOpen(false)} sx={{ color: "#fff" }}>
            Cancel
          </Button>
          <Button
            onClick={confirmExecute}
            variant="contained"
            disabled={loading}
            sx={{
              background: "linear-gradient(148.97deg, #2af598 0%, #00c820 100%)",
              color: "#000",
            }}
          >
            {loading ? <CircularProgress size={24} /> : `Convert to ${LEDGER_TYPES[targetType]}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LedgerStandardizationTool;

